/**
 * Image Gallery with Variant Hotspots
 * Modular vanilla JS — filters, deferred modal, hotspot/card sync, a11y
 */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

  /* --------------------------------------------------------------------------
   * Focus trap
   * ------------------------------------------------------------------------ */
  function FocusTrap(container) {
    this.container = container;
    this._onKeyDown = this._onKeyDown.bind(this);
  }

  FocusTrap.prototype.activate = function () {
    document.addEventListener('keydown', this._onKeyDown);
  };

  FocusTrap.prototype.deactivate = function () {
    document.removeEventListener('keydown', this._onKeyDown);
  };

  FocusTrap.prototype._onKeyDown = function (event) {
    if (event.key !== 'Tab') return;

    var focusable = Array.prototype.slice.call(
      this.container.querySelectorAll(FOCUSABLE)
    ).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });

    if (!focusable.length) {
      event.preventDefault();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* --------------------------------------------------------------------------
   * Filters
   * ------------------------------------------------------------------------ */
  function Filters(root) {
    this.root = root;
    this.buttons = root.querySelectorAll('[data-igh-filter]');
    this.items = root.querySelectorAll('[data-igh-item]');
    this._onClick = this._onClick.bind(this);
  }

  Filters.prototype.init = function () {
    var self = this;
    this.buttons.forEach(function (btn) {
      btn.addEventListener('click', self._onClick);
    });
  };

  Filters.prototype._onClick = function (event) {
    var button = event.currentTarget;
    var category = (button.getAttribute('data-igh-filter') || 'all').toLowerCase();

    // Sync filters across all gallery sections on the page (chunked galleries).
    document.querySelectorAll('[data-igh-filter]').forEach(function (btn) {
      var btnCat = (btn.getAttribute('data-igh-filter') || 'all').toLowerCase();
      btn.setAttribute('aria-pressed', btnCat === category ? 'true' : 'false');
    });

    document.querySelectorAll('[data-igh-item]').forEach(function (item) {
      var raw = item.getAttribute('data-category') || '';
      var parts = raw.split(',').map(function (s) {
        return s.trim().toLowerCase();
      }).filter(Boolean);
      var show = category === 'all' || parts.indexOf(category) !== -1;
      if (show) {
        item.removeAttribute('hidden');
      } else {
        item.setAttribute('hidden', '');
      }
    });
  };

  /* --------------------------------------------------------------------------
   * Modal — deferred content, hotspot ↔ card sync
   * ------------------------------------------------------------------------ */
  function Modal(root) {
    this.root = root;
    this.modal = root.querySelector('[data-igh-modal]');
    this.dialog = root.querySelector('[data-igh-dialog]');
    this.mediaEl = root.querySelector('[data-igh-modal-media]');
    this.sidebarEl = root.querySelector('[data-igh-modal-sidebar]');
    this.closeButtons = root.querySelectorAll('[data-igh-close]');
    this.focusTrap = new FocusTrap(this.dialog);
    this.lastFocus = null;
    this.activeHotspotId = null;
    this.markerPulse = root.getAttribute('data-marker-animation') === 'pulse';
    this.viewProductLabel = root.getAttribute('data-view-product-label') || 'View Product';

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onMediaClick = this._onMediaClick.bind(this);
    this._onSidebarOver = this._onSidebarOver.bind(this);
    this._onSidebarOut = this._onSidebarOut.bind(this);
    this._onSidebarFocusIn = this._onSidebarFocusIn.bind(this);
  }

  Modal.prototype.init = function () {
    var self = this;

    this.root.querySelectorAll('[data-igh-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        var item = trigger.closest('[data-igh-item]');
        if (!item) return;

        var preselect = null;
        var hotspotBtn = event.target.closest('[data-igh-hotspot]');
        if (hotspotBtn && trigger.contains(hotspotBtn)) {
          event.stopPropagation();
          preselect = hotspotBtn.getAttribute('data-hotspot-id');
        }

        self.open(item, preselect);
      });
    });

    this.closeButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.close();
      });
    });
  };

  Modal.prototype.open = function (item, preselectHotspotId) {
    var payload = this._getPayload(item);
    if (!payload) return;

    this.lastFocus = document.activeElement;
    this._activeItem = item;
    this._render(payload, preselectHotspotId || null);

    this.modal.removeAttribute('hidden');
    document.body.classList.add('igh-modal-open');
    document.addEventListener('keydown', this._onKeyDown);
    this.focusTrap.activate();

    var closeBtn = this.dialog.querySelector('[data-igh-close]');
    if (closeBtn) closeBtn.focus();
  };

  Modal.prototype.close = function () {
    if (this.modal.hasAttribute('hidden')) return;

    this.modal.setAttribute('hidden', '');
    document.body.classList.remove('igh-modal-open');
    document.removeEventListener('keydown', this._onKeyDown);
    this.focusTrap.deactivate();

    // Cache built nodes on the item for reopen (avoid rebuilding)
    if (this._activeItem) {
      if (this.mediaEl.firstChild) {
        this._activeItem._ighMediaCache = this.mediaEl.removeChild(this.mediaEl.firstChild);
      }
      if (this.sidebarEl.firstChild) {
        this._activeItem._ighSidebarCache = this.sidebarEl.removeChild(this.sidebarEl.firstChild);
      }
    }

    this.mediaEl.innerHTML = '';
    this.sidebarEl.innerHTML = '';
    this.activeHotspotId = null;
    this._activeItem = null;

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  };

  Modal.prototype._onKeyDown = function (event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  };

  Modal.prototype._getPayload = function (item) {
    if (item._ighPayload) return item._ighPayload;

    var script = item.querySelector('[data-igh-payload]');
    if (!script) return null;

    try {
      item._ighPayload = JSON.parse(script.textContent);
      return item._ighPayload;
    } catch (err) {
      console.warn('[image-gallery-hotspots] Invalid JSON payload', err);
      return null;
    }
  };

  Modal.prototype._render = function (payload, preselectHotspotId) {
    var self = this;
    var item = this._activeItem;

    // Reuse cached DOM when reopening the same image
    if (item && item._ighMediaCache && item._ighSidebarCache) {
      this.mediaEl.innerHTML = '';
      this.sidebarEl.innerHTML = '';
      this.mediaEl.appendChild(item._ighMediaCache);
      this.sidebarEl.appendChild(item._ighSidebarCache);
      item._ighMediaCache = null;
      item._ighSidebarCache = null;

      this.mediaEl.removeEventListener('click', this._onMediaClick);
      this.mediaEl.addEventListener('click', this._onMediaClick);
      this.sidebarEl.removeEventListener('mouseover', this._onSidebarOver);
      this.sidebarEl.removeEventListener('mouseout', this._onSidebarOut);
      this.sidebarEl.removeEventListener('focusin', this._onSidebarFocusIn);
      this.sidebarEl.addEventListener('mouseover', this._onSidebarOver);
      this.sidebarEl.addEventListener('mouseout', this._onSidebarOut);
      this.sidebarEl.addEventListener('focusin', this._onSidebarFocusIn);

      this._clearActive();
      if (preselectHotspotId) {
        this._setActive(preselectHotspotId, true);
      }
      return;
    }

    var image = payload.image || {};
    var hotspots = payload.hotspots || [];

    // Left: image + hotspots
    var wrap = document.createElement('div');
    wrap.className = 'igh__modal-image-wrap';

    var img = document.createElement('img');
    img.className = 'igh__modal-image';
    img.src = image.src || '';
    img.alt = image.alt || '';
    if (image.width) img.width = image.width;
    if (image.height) img.height = image.height;
    wrap.appendChild(img);

    var hotspotLayer = document.createElement('div');
    hotspotLayer.className = 'igh__modal-hotspots';
    hotspotLayer.setAttribute('data-igh-modal-hotspots', '');

    hotspots.forEach(function (h) {
      if (!h.variant) return;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'igh__hotspot' + (self.markerPulse ? ' igh__hotspot--pulse' : '');
      btn.setAttribute('data-igh-hotspot', '');
      btn.setAttribute('data-hotspot-id', h.id);
      btn.style.left = h.x + '%';
      btn.style.top = h.y + '%';
      btn.setAttribute(
        'aria-label',
        h.label || (h.variant.product_title + ' — ' + h.variant.title)
      );
      if (h.tooltip) {
        btn.setAttribute('data-tooltip', h.tooltip);
      } else if (h.variant && h.variant.product_title) {
        btn.setAttribute('data-tooltip', h.variant.product_title);
      }

      var icon = document.createElement('span');
      icon.className = 'igh__hotspot-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '+';
      btn.appendChild(icon);

      hotspotLayer.appendChild(btn);
    });

    wrap.appendChild(hotspotLayer);
    this.mediaEl.innerHTML = '';
    this.mediaEl.appendChild(wrap);

    // Right: product cards (only hotspot-referenced variants)
    var list = document.createElement('ul');
    list.className = 'igh__modal-products';
    list.setAttribute('data-igh-products', '');

    if (!hotspots.length) {
      var empty = document.createElement('p');
      empty.className = 'igh__modal-empty';
      empty.textContent = 'No products linked to this image.';
      this.sidebarEl.innerHTML = '';
      this.sidebarEl.appendChild(empty);
    } else {
      hotspots.forEach(function (h) {
        if (!h.variant) return;
        var v = h.variant;
        var li = document.createElement('li');
        li.className = 'igh__product-card';
        li.setAttribute('data-igh-card', '');
        li.setAttribute('data-hotspot-id', h.id);
        li.tabIndex = 0;

        var thumbHtml = v.image
          ? '<img class="igh__product-card-image" src="' +
            escapeAttr(v.image) +
            '" alt="' +
            escapeAttr(v.product_title || '') +
            '" width="72" height="72" loading="lazy">'
          : '<div class="igh__product-card-image igh__product-card-image--placeholder" aria-hidden="true"></div>';

        var variantTitle =
          v.title && v.title !== 'Default Title'
            ? '<p class="igh__product-card-variant">' + escapeHtml(v.title) + '</p>'
            : '';

        li.innerHTML =
          thumbHtml +
          '<div class="igh__product-card-body">' +
          '<a class="igh__product-card-title" href="' +
          escapeAttr(v.url || '#') +
          '" target="_blank" rel="noopener noreferrer">' +
          escapeHtml(v.product_title || '') +
          '</a>' +
          variantTitle +
          '<p class="igh__product-card-price">' +
          escapeHtml(v.price || '') +
          '</p>' +
          '</div>';

        list.appendChild(li);
      });

      this.sidebarEl.innerHTML = '';
      this.sidebarEl.appendChild(list);
    }

    // Bind sync events
    this.mediaEl.removeEventListener('click', this._onMediaClick);
    this.mediaEl.addEventListener('click', this._onMediaClick);

    this.sidebarEl.removeEventListener('mouseover', this._onSidebarOver);
    this.sidebarEl.removeEventListener('mouseout', this._onSidebarOut);
    this.sidebarEl.removeEventListener('focusin', this._onSidebarFocusIn);
    this.sidebarEl.addEventListener('mouseover', this._onSidebarOver);
    this.sidebarEl.addEventListener('mouseout', this._onSidebarOut);
    this.sidebarEl.addEventListener('focusin', this._onSidebarFocusIn);

    if (preselectHotspotId) {
      this._setActive(preselectHotspotId, true);
    }
  };

  Modal.prototype._clearActive = function () {
    this.activeHotspotId = null;
    this.mediaEl.querySelectorAll('[data-igh-hotspot]').forEach(function (el) {
      el.classList.remove('is-active');
    });
    this.sidebarEl.querySelectorAll('[data-igh-card]').forEach(function (el) {
      el.classList.remove('is-active');
    });
  };

  Modal.prototype._onMediaClick = function (event) {
    var hotspot = event.target.closest('[data-igh-hotspot]');
    if (!hotspot || !this.mediaEl.contains(hotspot)) return;
    event.preventDefault();
    this._setActive(hotspot.getAttribute('data-hotspot-id'), true);
  };

  Modal.prototype._onSidebarOver = function (event) {
    var card = event.target.closest('[data-igh-card]');
    if (!card || !this.sidebarEl.contains(card)) return;
    this._setActive(card.getAttribute('data-hotspot-id'), false);
  };

  Modal.prototype._onSidebarOut = function (event) {
    var card = event.target.closest('[data-igh-card]');
    if (!card) return;
    var related = event.relatedTarget;
    if (related && card.contains(related)) return;
    // Keep last active highlight; do not clear on mouse leave
  };

  Modal.prototype._onSidebarFocusIn = function (event) {
    var card = event.target.closest('[data-igh-card]');
    if (!card || !this.sidebarEl.contains(card)) return;
    this._setActive(card.getAttribute('data-hotspot-id'), false);
  };

  Modal.prototype._setActive = function (hotspotId, scrollCard) {
    if (!hotspotId) return;
    this.activeHotspotId = hotspotId;

    this.mediaEl.querySelectorAll('[data-igh-hotspot]').forEach(function (el) {
      el.classList.toggle('is-active', el.getAttribute('data-hotspot-id') === hotspotId);
    });

    var activeCard = null;
    this.sidebarEl.querySelectorAll('[data-igh-card]').forEach(function (el) {
      var match = el.getAttribute('data-hotspot-id') === hotspotId;
      el.classList.toggle('is-active', match);
      if (match) activeCard = el;
    });

    if (scrollCard && activeCard) {
      activeCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  /* --------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------ */
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/'/g, '&#39;');
  }

  /* --------------------------------------------------------------------------
   * Gallery root
   * ------------------------------------------------------------------------ */
  function Gallery(root) {
    this.root = root;
    this.filters = new Filters(root);
    this.modal = new Modal(root);
  }

  Gallery.prototype.init = function () {
    this.filters.init();
    this.modal.init();
  };

  function initAll() {
    document.querySelectorAll('[data-image-gallery-hotspots]').forEach(function (root) {
      if (root._ighInitialized) return;
      root._ighInitialized = true;
      new Gallery(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor section reload support
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;
    section.querySelectorAll('[data-image-gallery-hotspots]').forEach(function (root) {
      root._ighInitialized = false;
    });
    initAll();
  });
})();
